import { createSignal, createEffect, onCleanup, Show, onMount } from "solid-js";
import { Text, View, hexColor } from "@lightningtv/solid";
import { clientBranding } from "../../config/clientConfig";
import { useLocation } from "@solidjs/router";
import { Row } from "@lightningtv/solid-ui";
import { LazyUp } from "@lightningtv/solid/primitives";
import {
  isBuffering,
  playListPassword,
  playListUrl,
  playListUsername,
  setIsBuffering,
  setLastStream,
  setLiveTvStreamID,
  setPreviewToNotShow,
} from "../../../state";
import axios from "axios";
import { useNavigate } from "@solidjs/router";
import videoPlayer from "../../video";
import LoadingIndicator from "../loading/loading";
import { Buffer } from "buffer";
import ConfirmPin from "../../components/ConfirmPin";
import { dnsResolver } from "../../api/providers/dnsResolver";
import OSD from "./Osd";
import { initializeConfig } from "../../config/index";
import { buildStreamUrl, buildStreamUrlWithToken, fetchChannelToken } from "../../utils/tokenHelper";
import { initFastChannelSession, appendAvAdParams } from "../../utils/mediaTailoreHelper";
import { fixImageUrl } from "../../utils/fixImageUrl";
import EpgGuideView from "../../components/EpgGuideView";
import ChannelCarousel from "./ChannelCarousel";

const LiveTvPage = () => {
  const navigate = useNavigate();
  const [data, setData] = createSignal<any>([]);
  const [liveChannel, setLiveChannel] = createSignal<any>({});
  const [currentIndex, setCurrentIndex] = createSignal(0);
  const [currentStream, setCurrentStream] = createSignal();
  const [isVideoPlaying, setIsVideoPlaying] = createSignal(false);
  const [showOSD, setShowOSD] = createSignal(false);
  const [isFromSearch, setIsFromSearch] = createSignal(null);
  const [isChannelLocked, setIsChannelLocked] = createSignal(false);
  const [showPinEntry, setShowPinEntry] = createSignal(false);
  const [isAndroidMode, setIsAndroidMode] = createSignal(false);
  const [channelChangeInProgress, setChannelChangeInProgress] = createSignal(false);
  const [showChannelMenu, setShowChannelMenu] = createSignal(false);
  const [selectedMenuIndex, setSelectedMenuIndex] = createSignal(0);
  const [autoPlayTriggered, setAutoPlayTriggered] = createSignal(false);
  const [pendingChannelLoad, setPendingChannelLoad] = createSignal<any>(null);
  const [adultPinVerified, setAdultPinVerified] = createSignal(false);
  const [isNavigatingAway, setIsNavigatingAway] = createSignal(false);
  const [pinErrorMessage, setPinErrorMessage] = createSignal("");
  const [channelBeforePinEntry, setChannelBeforePinEntry] = createSignal<number | null>(null);
  const [showChannelCarousel, setShowChannelCarousel] = createSignal(false);

  const [showExitToast, setShowExitToast] = createSignal(false);
  const [escapePressed, setEscapePressed] = createSignal(false);

  // ── Numeric channel dial ──────────────────────────────────────────
  const [numericInput, setNumericInput] = createSignal("");
  let numInputTimer: any = null;

  // ── Catchup signals ───────────────────────────────────────────────
  const [showCatchupCarousel, setShowCatchupCarousel] = createSignal(false);
  const [catchupProgram, setCatchupProgram] = createSignal<any>(null);
  const [isPlayingCatchup, setIsPlayingCatchup] = createSignal(false);

  let timeoutId;
  let channelMenuRef: any = null;
  let osdForceShowTimeout: any;
  let rootRef: any = null;
  const location = useLocation();
  let exitToastTimeout: any;
  let carouselHideTimer: any = null;
  let carouselPlayDebounce: any = null;
  let carouselIsPlaying = false;
  let carouselStreamStarted = false; // true once a stream has been kicked off for this carousel session

  const cleanUrl = (url: any) => (typeof url === "string" ? url.trim() : "");
  const isValidDirectSource = (url: any) => {
    const u = cleanUrl(url);
    return u.startsWith("http") && u.length > 15 && u !== "https://";
  };

  const isAndroidWebView = () => window.AndroidVideoPlayer !== undefined;

  // ── Does this channel have catchup? ──────────────────────────────
  // For testing: always returns true. For production, use the commented version.
  const channelHasCatchup = (channel: any) => {
    // return true; // TEST MODE — remove this line for production
    if (!channel) return false;

    return (
      channel.tv_archive === 1 ||
      channel.tv_archive === "1" ||
      channel.tv_archive === true ||
      Number(channel.tv_archive_duration) > 0
    );
  };

  const isAdultChannel = (channel: any) => {
    if (!channel) return false;
    const rawCat = channel.category_name ?? channel.categoryName ?? channel.category ?? "";
    const cat = String(rawCat).trim().toLowerCase();
    return cat === "adults" || cat === "adult" || cat.includes("adult") || cat.includes("xxx") || cat.includes("18+");
  };

  const needsPinVerification = (channel: any) => {
    return isAdultChannel(channel) && !adultPinVerified();
  };

  const findNextNonAdultChannel = (startIndex: number, direction: "up" | "down" = "down") => {
    const channels = data();
    if (channels.length === 0) return 0;
    if (direction === "down") {
      for (let i = startIndex + 1; i < channels.length; i++) { if (!isAdultChannel(channels[i])) return i; }
      for (let i = 0; i < startIndex; i++) { if (!isAdultChannel(channels[i])) return i; }
    } else {
      for (let i = startIndex - 1; i >= 0; i--) { if (!isAdultChannel(channels[i])) return i; }
      for (let i = channels.length - 1; i > startIndex; i--) { if (!isAdultChannel(channels[i])) return i; }
    }
    return 0;
  };

  onMount(() => {
    console.log("📺 LiveTV page mounted");
    initializeConfig();
    if (isAndroidWebView()) {
      console.log("📱 Android bridge active");
      setIsAndroidMode(true);
      window.androidVideoEvents = {
        onVideoReady: () => {
          setIsBuffering(false);
          setIsVideoPlaying(true);
          setShowOSD(true);
          setTimeout(() => setShowOSD(true), 500);
          setTimeout(() => { setShowOSD(true); resetOSDTimeout(); }, 1000);
          setTimeout(() => setShowOSD(true), 1500);
        },
        onError: (error) => {
          console.error("📱 Video error from Android:", error);
          setIsBuffering(false);
          setIsVideoPlaying(false);
        }
      };
      window.triggerOSDShow = () => {
        setShowOSD(true);
        resetOSDTimeout();
      };
    }

    // ── Reconnect handler: reload stream when internet is verified back ──
    // Listens to 'connectivity-restored' dispatched by App.tsx only after
    // the HTTP probe confirms real connectivity — not the raw 'online' event
    // which fires before the network is usable on real Android TV hardware.
    const onConnectionRestored = () => {
      console.log("🌐 connectivity-restored in LiveTV — scheduling stream reload");
      // Re-read liveChannel() inside the timeout — do NOT capture it at
      // listener-registration time. On real devices the closure can hold a
      // stale value if the component re-rendered between mount and reconnect.
      setTimeout(() => {
        const ch = liveChannel();
        if (!ch?.stream_id) {
          console.warn("🌐 No channel to reload after reconnect");
          return;
        }
        console.log("🌐 Reloading stream:", ch.stream_id, ch.name);
        setIsBuffering(true);
        setShowOSD(false);
        loadNewStream(ch.stream_id);
        // Fallback OSD trigger — ExoPlayer fires onVideoReady which calls
        // androidVideoEvents.onVideoReady → showOSD, but if that callback
        // is slow on this device, we force the OSD visible after 4 s.
        setTimeout(() => {
          if (isVideoPlaying() || !isBuffering()) {
            setShowOSD(true);
            resetOSDTimeout();
            console.log("🌐 Fallback OSD shown after reconnect");
          }
        }, 4000);
      }, 1500);
    };
    window.addEventListener("connectivity-restored", onConnectionRestored);
    onCleanup(() => window.removeEventListener("connectivity-restored", onConnectionRestored));

    // Numeric key listener
    const onNumericKey = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") {
        if (!showPinEntry() && !showChannelMenu() && !showCatchupCarousel()) {
          handleNumericInput(e.key);
        }
      }
      if (e.key === "Enter" && numericInput()) {
        e.stopPropagation();
        commitNumericInput();
      }
    };
    document.addEventListener("keydown", onNumericKey, true);
    onCleanup(() => document.removeEventListener("keydown", onNumericKey, true));

    const onChannelNavKeyUp = (e: KeyboardEvent) => {
      if (showChannelCarousel() && (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "PageUp" || e.key === "PageDown")) {
        clearTimeout(carouselPlayDebounce);
        triggerCarouselPlay();
      }
    };
    document.addEventListener("keyup", onChannelNavKeyUp, true);
    onCleanup(() => document.removeEventListener("keyup", onChannelNavKeyUp, true));
  });

  onCleanup(() => {
    console.log("🧹 LiveTV cleanup - destroying player");
    clearTimeout(timeoutId);
    clearTimeout(osdForceShowTimeout);
    clearTimeout(exitToastTimeout);
    clearTimeout(numInputTimer);
    clearTimeout(carouselHideTimer);
    clearTimeout(carouselPlayDebounce);
    if (isAndroidWebView() && window.AndroidVideoPlayer) {
      window.AndroidVideoPlayer.enableLiveTVMode(false);
      window.AndroidVideoPlayer.stopVideo();
      delete window.handleAndroidKeyPress;
      delete window.androidVideoEvents;
    } else {
      videoPlayer.destroy();
    }
  });

  const handleExitNavigation = async () => {
    console.log("🚪 Exiting LiveTV...");
    setIsNavigatingAway(true);
    try {
      setShowPinEntry(false);
      setPendingChannelLoad(null);
      setIsChannelLocked(false);
      setAdultPinVerified(false);
      if (window.AndroidVideoPlayer && typeof window.AndroidVideoPlayer.stopVideo === "function") {
        window.AndroidVideoPlayer.stopVideo();
      } else {
        videoPlayer.destroy?.();
      }
      if (window.AndroidVideoPlayer && typeof window.AndroidVideoPlayer.enableLiveTVMode === "function") {
        window.AndroidVideoPlayer.enableLiveTVMode(false);
      }
      await new Promise((r) => setTimeout(r, 200));
      navigate("/home");
    } catch (err) {
      console.error("Escape handling error:", err);
      setIsNavigatingAway(false);
      navigate("/home");
    }
  };

  function getQueryParams(search) {
    return new URLSearchParams(search);
  }

  const tryFocusMenu = () => {
    try {
      if (!channelMenuRef) return false;
      if (typeof channelMenuRef.setFocus === "function") { channelMenuRef.setFocus(); return true; }
      if (typeof channelMenuRef.focus === "function") { channelMenuRef.focus(); return true; }
      if (typeof channelMenuRef.setActive === "function") { channelMenuRef.setActive(); return true; }
      return false;
    } catch (err) { console.warn("⚠ tryFocusMenu failed:", err); return false; }
  };

  const tryBlurMenu = () => {
    try {
      if (!channelMenuRef) return;
      if (typeof channelMenuRef.blur === "function") { channelMenuRef.blur(); return true; }
      return false;
    } catch (err) { console.warn("⚠ tryBlurMenu failed:", err); return false; }
  };

  const restoreRootFocus = () => {
    try {
      if (rootRef) {
        if (typeof rootRef.setFocus === "function") { rootRef.setFocus(); return; }
        if (typeof rootRef.focus === "function") { rootRef.focus(); return; }
      }
      document.body?.focus?.();
    } catch (err) { console.warn("⚠ restoreRootFocus failed:", err); }
  };

  const openChannelMenu = () => {
    // Close catchup carousel first if open
    if (showCatchupCarousel()) {
      setShowCatchupCarousel(false);
    }
    if (showChannelCarousel()) {
      clearTimeout(carouselHideTimer);
      clearTimeout(carouselPlayDebounce);
      setShowChannelCarousel(false);
    }
    clearTimeout(timeoutId);
    setSelectedMenuIndex(currentIndex());
    setShowChannelMenu(true);
    setShowOSD(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const ok = tryFocusMenu();
        if (!ok) setTimeout(() => { tryFocusMenu(); }, 80);
      });
    });
  };

  const closeChannelMenu = (restoreOSD = true) => {
    tryBlurMenu();
    channelMenuRef = null;
    setShowChannelMenu(false);
    requestAnimationFrame(() => {
      restoreRootFocus();
      if (restoreOSD) {
        setTimeout(() => {
          setShowOSD(true);
          resetOSDTimeout();
        }, 60);
      }
    });
  };

  createEffect(() => {
    const params = getQueryParams(location.search);
    const numParam = params.get("num");
    const isTrue = params.get("isTrue") === "true";
    const isLocked = params.get("isLocked") === "true";
    const fromHome = params.get("fromHome") === "true";

    setIsFromSearch(isTrue);
    setIsChannelLocked(isLocked);

    if (data().length === 0) return;
    if (autoPlayTriggered()) return;

    const channelIndex = data().findIndex(
      (channel) => String(channel.stream_id) === String(numParam)
    );

    if (channelIndex !== -1) {
      const selectedChannel = data()[channelIndex];
      setCurrentIndex(channelIndex);
      setLiveChannel(selectedChannel);
      setCurrentStream(selectedChannel.stream_id);

      if (isAndroidWebView() && window.AndroidVideoPlayer) {
        window.AndroidVideoPlayer.setChannelInfo(
          selectedChannel.name || "Unknown Channel",
          selectedChannel.num?.toString() || "0"
        );
        window.AndroidVideoPlayer.setCurrentChannelIndex(channelIndex);
      }

      if (fromHome || isTrue) {
        setAutoPlayTriggered(true);
        setTimeout(() => {
          if (needsPinVerification(selectedChannel)) {
            setIsChannelLocked(true);
            setShowPinEntry(true);
            setPendingChannelLoad(selectedChannel);
          } else if (isLocked) {
            setShowPinEntry(true);
            setPendingChannelLoad(selectedChannel);
          } else {
            onEnterTrailer("background");
          }
        }, 500);
      }
    } else {
      if (data().length > 0) {
        const firstChannel = data()[0];
        setCurrentIndex(0);
        setLiveChannel(firstChannel);
        setCurrentStream(firstChannel.stream_id);
        if (fromHome) {
          setAutoPlayTriggered(true);
          setTimeout(() => {
            if (needsPinVerification(firstChannel)) {
              setIsChannelLocked(true);
              setShowPinEntry(true);
              setPendingChannelLoad(firstChannel);
            } else {
              onEnterTrailer("background");
            }
          }, 500);
        }
      }
    }
  });

  createEffect(async () => {
    const username = playListUsername();
    const password = playListPassword();
    const playList = playListUrl();
    const vpnStatus = localStorage.getItem("vpn");
    const proxyUrl = localStorage.getItem("proxy_url");

    let originalApiUrl = `${playList}/player_api.php?username=${username}&password=${password}&action=get_live_streams`;
    let finalApiUrl = originalApiUrl;
    let headers = {};

    try {
      const dnsResult = await dnsResolver.transformUrl(originalApiUrl);
      if (typeof dnsResult === 'object' && dnsResult.url) {
        finalApiUrl = dnsResult.url;
        headers['Host'] = dnsResult.originalHost;
      } else if (typeof dnsResult === 'string' && dnsResult !== originalApiUrl) {
        finalApiUrl = dnsResult;
      }
    } catch (dnsError) {
      finalApiUrl = originalApiUrl;
    }

    if (vpnStatus === "on" && proxyUrl) {
      finalApiUrl = `${proxyUrl}${Buffer.from(finalApiUrl, "utf-8").toString("base64")}`;
    }

    axios
      .get(finalApiUrl, { headers })
      .then(response => {
        const sorted = [...(response.data || [])].sort((a: any, b: any) => (a.num || 0) - (b.num || 0));
        setData(sorted);

        if (sorted.length > 0) {
          console.log("🔍 SAMPLE CHANNEL DATA (first 3 channels):");
          sorted.slice(0, 3).forEach((channel: any, index: number) => {
            console.log(`Channel ${index}:`, { name: channel.name, num: channel.num, stream_id: channel.stream_id, category_name: channel.category_name });
          });
        }

        console.log("✅ LiveTV API OK — total channels:", sorted.length);

        if (isAndroidWebView() && window.AndroidVideoPlayer && sorted.length > 0) {
          const initialIndex = currentIndex();
          if (sorted[initialIndex]) {
            window.AndroidVideoPlayer.setChannelInfo(sorted[initialIndex].name || "Unknown Channel", sorted[initialIndex].num?.toString() || "0");
          }
          window.AndroidVideoPlayer.setCurrentChannelIndex(initialIndex);
        }

        const params: any = getQueryParams(location.search);
        const fromHome: any = params.get("fromHome") === "true";

        if (fromHome && !autoPlayTriggered() && sorted.length > 0) {
          setAutoPlayTriggered(true);
          const firstChannel = sorted[0];
          setCurrentStream(firstChannel.stream_id);
          setLiveChannel(firstChannel);
          setTimeout(() => {
            if (needsPinVerification(firstChannel)) {
              setIsChannelLocked(true);
              setShowPinEntry(true);
              setPendingChannelLoad(firstChannel);
            } else {
              onEnterTrailer("background");
            }
          }, 800);
        }
      })
      .catch(error => {
        console.error("⚠ LiveTV API call failed:", error);
      });
  });

  createEffect(() => {
    const index = currentIndex();
    const channelData = data();
    if (channelData.length > 0 && index >= 0 && index < channelData.length) {
      const newChannel = channelData[index];
      setLiveChannel(newChannel);
      if (isAndroidWebView() && window.AndroidVideoPlayer) {
        window.AndroidVideoPlayer.setChannelInfo(newChannel.name || "Unknown Channel", newChannel.num?.toString() || "0");
        window.AndroidVideoPlayer.setCurrentChannelIndex(index);
      }
    }
  });

  const incrementIndex = async () => {
    if (showPinEntry()) return;
    if (channelChangeInProgress()) return;
    const newIndex = currentIndex() + 1;
    if (newIndex < data().length) {
      setChannelChangeInProgress(true);
      const newChannel = data()[newIndex];
      setCurrentIndex(newIndex);
      setLiveChannel(newChannel);
      setShowOSD(true);
      if (needsPinVerification(newChannel)) {
        setChannelBeforePinEntry(newIndex - 1);
        setIsChannelLocked(true);
        setShowPinEntry(true);
        setPendingChannelLoad(newChannel);
        setChannelChangeInProgress(false);
      } else {
        await loadNewStream(newChannel.stream_id);
        setIsBuffering(true);
        // Don't restart the auto-hide countdown here — the stream isn't ready
        // yet. Just cancel any pending hide from a previous channel; the real
        // "video ready" signal (onVideoReady / playCurrentStream) will call
        // resetOSDTimeout() once playback actually starts.
        clearTimeout(timeoutId);
        setTimeout(() => setChannelChangeInProgress(false), 1000);
      }
    }
  };

  const decrementIndex = async () => {
    if (showPinEntry()) return;
    if (channelChangeInProgress()) return;
    const newIndex = currentIndex() - 1;
    if (newIndex >= 0) {
      setChannelChangeInProgress(true);
      const newChannel = data()[newIndex];
      setCurrentIndex(newIndex);
      setLiveChannel(newChannel);
      setShowOSD(true);
      if (needsPinVerification(newChannel)) {
        setChannelBeforePinEntry(newIndex + 1);
        setIsChannelLocked(true);
        setShowPinEntry(true);
        setPendingChannelLoad(newChannel);
        setChannelChangeInProgress(false);
      } else {
        await loadNewStream(newChannel.stream_id);
        setIsBuffering(true);
        clearTimeout(timeoutId);
        setTimeout(() => setChannelChangeInProgress(false), 1000);
      }
    }
  };

  const loadNewStream = async (streamId: any) => {
    // Reset catchup state whenever a new live stream loads
    setIsPlayingCatchup(false);
    setCatchupProgram(null);
    setShowCatchupCarousel(false);

    setCurrentStream(streamId);
    setLastStream(streamId);

    const currentChannelData = data().find((ch: any) => ch.stream_id === streamId);
    if (!currentChannelData) {
      console.error("❌ Channel data not found for stream:", streamId);
      return;
    }
    console.log("📡 fast channel direct urlllllllll 1111:");

    if (isValidDirectSource(currentChannelData.direct_source)) {
      let directUrl = cleanUrl(currentChannelData.direct_source);
      console.log("📡 fast channel direct urlllllllll:", directUrl);


      if (currentChannelData.channel_type === "fast_channel") {
        console.log("📡 fast channel direct urlllllllll 2222:", currentChannelData);
        directUrl = await initFastChannelSession(directUrl, currentChannelData);
      } else {
        directUrl = await appendAvAdParams(directUrl, currentChannelData);
      }
      await playCurrentStream(directUrl);
      return;
    }

    let tokenData: any = null;
    try {
      tokenData = await fetchChannelToken(currentChannelData);
    } catch (error) {
      console.error("❌ Token fetch error:", error);
    }

    let streamUrl: string = tokenData && currentChannelData.direct_source
      ? buildStreamUrl(currentChannelData, "", tokenData)
      : `${playListUrl()}/live/${playListUsername()}/${playListPassword()}/${streamId}.m3u8`;

    let finalStreamUrl = streamUrl;
    if (!isValidDirectSource(currentChannelData.direct_source) && !tokenData) {
      try {
        const dnsResult = await dnsResolver.transformUrl(streamUrl);
        if (typeof dnsResult === "object" && dnsResult.url) finalStreamUrl = dnsResult.url;
        else if (typeof dnsResult === "string" && dnsResult !== streamUrl) finalStreamUrl = dnsResult;
      } catch { finalStreamUrl = streamUrl; }
    }

    if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
      finalStreamUrl = localStorage.getItem("proxy_url") + Buffer.from(finalStreamUrl, "utf-8").toString("base64");
    }

    if (currentChannelData.channel_type === "fast_channel") {
      finalStreamUrl = await initFastChannelSession(finalStreamUrl, currentChannelData);
      console.log("📡 fast channel url:", finalStreamUrl);
    } else {
      finalStreamUrl = await appendAvAdParams(finalStreamUrl, currentChannelData);
    }

    await playCurrentStream(finalStreamUrl);
  };

  const playCurrentStream = async (streamUrl) => {
    const finalUrl = cleanUrl(streamUrl);
    try {
      if (isAndroidWebView() && window.AndroidVideoPlayer) {
        const currentChannelData = liveChannel();
        if (currentChannelData) {
          window.AndroidVideoPlayer.setChannelInfo(currentChannelData.name || "Unknown Channel", currentChannelData.num?.toString() || "0");
        }
        window.AndroidVideoPlayer.playVideo(finalUrl, "fullscreen");
        return;
      }
      videoPlayer.destroy();
      await videoPlayer.playVideo(finalUrl, "fullscreen");
      setIsVideoPlaying(true);
      setIsBuffering(false);
      setShowOSD(true);
      resetOSDTimeout();
    } catch (error) {
      console.error("⚠ Error playing stream:", error);
      setIsVideoPlaying(false);
      setIsBuffering(false);
    }
  };

  // ── Catchup: play a recorded program ─────────────────────────────
  const handlePlayCatchup = async (program: any) => {
    console.log("📼 Playing catchup program:", program);
    setShowCatchupCarousel(false);
    setCatchupProgram(program);
    setIsPlayingCatchup(true);
    setIsBuffering(true);

    let streamUrl: string = program.stream_url;
    if (!streamUrl) {
      console.error("❌ Catchup program has no stream_url");
      setIsBuffering(false);
      setIsPlayingCatchup(false);
      return;
    }

    if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
      streamUrl = localStorage.getItem("proxy_url") + Buffer.from(streamUrl, "utf-8").toString("base64");
    }

    if (isAndroidWebView() && window.AndroidVideoPlayer) {
      window.AndroidVideoPlayer.enableLiveTVMode(false);
      window.AndroidVideoPlayer.setChannelInfo(liveChannel()?.name || "Catchup", liveChannel()?.num?.toString() || "0");
      window.AndroidVideoPlayer.playVideo(streamUrl, "fullscreen");
      setIsBuffering(false);
      setIsVideoPlaying(true);
      setShowOSD(true);
      resetOSDTimeout();
    } else {
      videoPlayer.destroy();
      videoPlayer.playVideo(streamUrl, "fullscreen")
        .then(() => {
          setIsBuffering(false);
          setIsVideoPlaying(true);
          setShowOSD(true);
          resetOSDTimeout();
        })
        .catch((e: any) => {
          console.error("❌ Catchup playback error:", e);
          setIsBuffering(false);
          setIsPlayingCatchup(false);
        });
    }
  };

  // ── Catchup: return to live stream ───────────────────────────────
  const returnToLive = () => {
    console.log("📺 Returning to live TV from catchup");
    setShowCatchupCarousel(false);
    setCatchupProgram(null);
    setIsPlayingCatchup(false);
    if (isAndroidWebView() && window.AndroidVideoPlayer) {
      window.AndroidVideoPlayer.enableLiveTVMode(true);
    }
    loadNewStream(liveChannel()?.stream_id);
    setShowOSD(true);
    // Cancel any pending hide rather than rescheduling one — the stream just
    // kicked off isn't ready yet; the real ready signal restarts the timer.
    clearTimeout(timeoutId);
  };

  function onCustomEscape() {
    setLiveTvStreamID(null);
    setIsBuffering(false);
    setPreviewToNotShow(true);
    if (isAndroidWebView() && window.AndroidVideoPlayer) {
      window.AndroidVideoPlayer.stopVideo();
      window.AndroidVideoPlayer.enableLiveTVMode(false);
    } else {
      videoPlayer.destroy();
    }
    navigate(-1);
  }

  // ── Guard: prevents bubbled Enter from carousel cards reaching root ─
  // When a card fires onPlayCatchup → the carousel closes → Enter bubbles
  // up to this handler in the same tick. The flag blocks that.
  let catchupEnterGuard = false;

  const lockCatchupEnter = () => {
    catchupEnterGuard = true;
    setTimeout(() => { catchupEnterGuard = false; }, 300);
  };

  const handleRootEnter = () => {
    // A carousel card just fired — ignore this bubbled Enter
    if (catchupEnterGuard) return;

    // Channel carousel open — Enter plays the selected channel immediately
    if (showChannelCarousel()) {
      clearTimeout(carouselHideTimer);
      clearTimeout(carouselPlayDebounce);
      setShowChannelCarousel(false);
      triggerCarouselPlay();
      return;
    }

    // PIN or channel menu active — don't interfere
    if (showPinEntry() || showChannelMenu()) return;

    // EpgGuideView is visible — close it on Enter
    if (showOSD()) {
      setShowOSD(false);
      clearTimeout(timeoutId);
      return;
    }

    // Otherwise show/refresh the EpgGuideView
    if (isVideoPlaying()) {
      setShowOSD(true);
      resetOSDTimeout();
    }
  };

  async function onEnterTrailer(context: any) {
    const streamOfID = currentStream();
    const currentChannelData = liveChannel();

    if (!currentChannelData || !streamOfID) {
      console.error("❌ Missing channel data or stream ID");
      return;
    }

    if (isValidDirectSource(currentChannelData.direct_source)) {
      let ds = cleanUrl(currentChannelData.direct_source);
      if (currentChannelData.channel_type === "fast_channel") {
        ds = await initFastChannelSession(ds, currentChannelData);
      } else {
        ds = await appendAvAdParams(ds, currentChannelData);
      }
      if (isAndroidWebView() && window.AndroidVideoPlayer) {
        window.AndroidVideoPlayer.setChannelInfo(currentChannelData.name || "Unknown Channel", currentChannelData.num?.toString() || "0");
        window.AndroidVideoPlayer.playVideo(ds, "fullscreen");
      } else {
        videoPlayer.playVideo(ds, context).catch((e) => console.error("⚠ Error playing direct_source:", e));
      }
      setIsVideoPlaying(true);
      setIsBuffering(false);
      setShowOSD(true);
      resetOSDTimeout();
      return;
    }

    let tokenData: any = null;
    try {
      tokenData = await fetchChannelToken(currentChannelData);
    } catch (error) {
      console.error("❌ Token fetch error:", error);
    }

    let videoUrl: string = tokenData && currentChannelData.direct_source
      ? buildStreamUrl(currentChannelData, "", tokenData)
      : `${playListUrl()}/live/${playListUsername()}/${playListPassword()}/${streamOfID}.m3u8`;

    let finalVideoUrl = videoUrl;
    if (!tokenData) {
      try {
        const dnsResult = await dnsResolver.transformUrl(videoUrl);
        if (typeof dnsResult === "object" && dnsResult.url) finalVideoUrl = dnsResult.url;
        else if (typeof dnsResult === "string" && dnsResult !== videoUrl) finalVideoUrl = dnsResult;
      } catch { finalVideoUrl = videoUrl; }
    }

    if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
      finalVideoUrl = localStorage.getItem("proxy_url") + Buffer.from(finalVideoUrl, "utf-8").toString("base64");
    }

    if (currentChannelData.channel_type === "fast_channel") {
      finalVideoUrl = await initFastChannelSession(finalVideoUrl, currentChannelData);
      console.log("📡 fast channel url:", finalVideoUrl);
    } else {
      finalVideoUrl = await appendAvAdParams(finalVideoUrl, currentChannelData);
    }

    if (isAndroidWebView() && window.AndroidVideoPlayer) {
      window.AndroidVideoPlayer.setChannelInfo(currentChannelData.name || "Unknown Channel", currentChannelData.num?.toString() || "0");
      window.AndroidVideoPlayer.playVideo(finalVideoUrl, "fullscreen");
      setIsVideoPlaying(true);
      setIsBuffering(false);
      setShowOSD(true);
      resetOSDTimeout();
      setTimeout(() => { setShowOSD(true); resetOSDTimeout(); }, 500);
    } else {
      videoPlayer.playVideo(finalVideoUrl, context)
        .then(() => {
          setIsBuffering(false);
          setIsVideoPlaying(true);
          setShowOSD(true);
          resetOSDTimeout();
          setTimeout(() => { setShowOSD(true); resetOSDTimeout(); }, 500);
        })
        .catch((error) => {
          console.error("⚠ Error playing trailer:", error);
          setIsVideoPlaying(false);
          setIsBuffering(false);
        });
    }
  }

  const handlePinConfirmed = () => {
    setShowPinEntry(false);
    setIsChannelLocked(false);
    setPinErrorMessage("");
    setChannelBeforePinEntry(null);
    setAdultPinVerified(true);
    const pending = pendingChannelLoad();
    if (pending) {
      if (showChannelMenu()) closeChannelMenu(false);
      onEnterTrailer("background");
      setPendingChannelLoad(null);
    }
  };

  const handlePinFailed = () => {
    setPinErrorMessage("Incorrect PIN. Please try again.");
    setTimeout(() => setPinErrorMessage(""), 3000);
  };

  createEffect(() => {
    const current = currentStream();
    if (current) {
      if (isFromSearch() === true) {
        const currentChannelData = liveChannel();
        if (needsPinVerification(currentChannelData)) {
          setIsChannelLocked(true);
          setShowPinEntry(true);
          setPendingChannelLoad(currentChannelData);
        } else if (isChannelLocked()) {
          setShowPinEntry(true);
          setPendingChannelLoad(currentChannelData);
        } else {
          onEnterTrailer("background");
        }
      }
    }
  });

  const resetOSDTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (!showChannelMenu()) setShowOSD(false);
    }, 2000);
  };

  const triggerCarouselPlay = () => {
    if (carouselIsPlaying) return;
    carouselIsPlaying = true;
    carouselStreamStarted = true; // mark stream as started so the hide-timer won't restart it
    clearTimeout(carouselPlayDebounce);
    clearTimeout(carouselHideTimer); // cancel the auto-hide timer — we're handling it now
    setShowChannelCarousel(false);   // close the carousel immediately so the OSD can appear
    const ch = data()[currentIndex()];
    if (!ch) { carouselIsPlaying = false; carouselStreamStarted = false; return; }
    if (needsPinVerification(ch)) {
      setChannelBeforePinEntry(currentIndex());
      setIsChannelLocked(true);
      setShowPinEntry(true);
      setPendingChannelLoad(ch);
      carouselIsPlaying = false;
      carouselStreamStarted = false;
    } else {
      loadNewStream(ch.stream_id);
      setIsBuffering(true);
      setShowOSD(true);
      clearTimeout(timeoutId);
      setTimeout(() => { carouselIsPlaying = false; }, 1500);
    }
  };

  const showCarouselAndNavigate = (direction: "up" | "down") => {
    if (showPinEntry()) return;
    carouselIsPlaying = false;
    carouselStreamStarted = false; // reset for this new navigation session
    const delta = direction === "up" ? 1 : -1;
    const newIndex = currentIndex() + delta;
    if (newIndex >= 0 && newIndex < data().length) {
      setCurrentIndex(newIndex);
      setLiveChannel(data()[newIndex]);
    }
    setShowChannelCarousel(true);
    clearTimeout(carouselHideTimer);
    carouselHideTimer = setTimeout(() => {
      setShowChannelCarousel(false);
      // Only start the stream if it hasn't already been kicked off via keyUp/debounce
      if (!carouselStreamStarted) triggerCarouselPlay();
    }, 5000);
    clearTimeout(carouselPlayDebounce);
    carouselPlayDebounce = setTimeout(() => {
      if (showChannelCarousel()) triggerCarouselPlay();
    }, 800);
  };

  const handleNumericInput = (digit: string) => {
    if (showPinEntry() || showChannelMenu() || showCatchupCarousel()) return;
    const next = (numericInput() + digit).slice(-4);
    setNumericInput(next);
    setShowOSD(false);
    clearTimeout(numInputTimer);
    numInputTimer = setTimeout(() => { commitNumericInput(); }, 2000);
  };

  const commitNumericInput = () => {
    const input = numericInput();
    setNumericInput("");
    clearTimeout(numInputTimer);
    if (!input) return;
    const targetNum = parseInt(input, 10);
    const channels = data();
    const idx = channels.findIndex((ch: any) => Number(ch.num) === targetNum);
    if (idx !== -1) {
      const ch = channels[idx];
      console.log(`📺 Numeric dial → ch ${targetNum}: ${ch.name}`);
      setCurrentIndex(idx);
      setLiveChannel(ch);
      if (needsPinVerification(ch)) {
        setChannelBeforePinEntry(currentIndex());
        setIsChannelLocked(true);
        setShowPinEntry(true);
        setPendingChannelLoad(ch);
      } else {
        loadNewStream(ch.stream_id);
        setIsBuffering(true);
        setShowOSD(true);
        clearTimeout(timeoutId);
      }
    } else {
      console.warn(`📺 No channel found for num: ${targetNum}`);
      setShowOSD(true);
      resetOSDTimeout();
    }
  };

  createEffect(() => {
    if (isVideoPlaying() && !showPinEntry() && !showChannelMenu()) {
      setShowOSD(true);
      osdForceShowTimeout = setTimeout(() => {
        if (isVideoPlaying() && !showChannelMenu()) setShowOSD(true);
      }, 500);
    }
    onCleanup(() => clearTimeout(osdForceShowTimeout));
  });

  createEffect(() => {
    if (showChannelMenu()) {
      console.log("🎬 Channel menu OPEN — channels:", data().length, "selected:", selectedMenuIndex());
    }
  });

  function ChannelItem(props: any) {
    return (
      <View
        onEnter={() => {
          const channelData = props.channel;
          setCurrentIndex(props.index);
          setLiveChannel(channelData);
          loadNewStream(channelData.stream_id);
          closeChannelMenu(true);
        }}
        onFocus={() => { console.log("🔍 Channel focused:", props.channel.name); }}
        style={{
          width: 450,
          height: 180,
          borderRadius: 15,
          color: props.index === props.currentIndex ? hexColor("#128DD380") : hexColor("#2a384480"),
          focus: { color: hexColor("#128DD3") }
        }}
      >
        <Text style={{ fontSize: 24, color: hexColor("#ffffff"), fontWeight: "bold" }} x={20} y={20}>
          {props.channel.num}. {props.channel.name}
        </Text>
      </View>
    );
  }

  return (
    <View
      ref={el => (rootRef = el)}
      width={1920}
      height={1080}
      color={0x00000000}
      onEscape={async () => {
        // 1. Cancel numeric input
        if (numericInput()) {
          setNumericInput("");
          clearTimeout(numInputTimer);
          setShowOSD(true);
          resetOSDTimeout();
          return;
        }

        // 2. Close channel carousel
        if (showChannelCarousel()) {
          clearTimeout(carouselHideTimer);
          clearTimeout(carouselPlayDebounce);
          setShowChannelCarousel(false);
          return;
        }

        // 3. Close catchup carousel (carousel's own onEscape also calls onClose,
        //    but we keep this here as a safety net)
        if (showCatchupCarousel()) {
          setShowCatchupCarousel(false);
          setShowOSD(true);
          resetOSDTimeout();
          return;
        }

        // 3. Return to live from catchup playback
        if (isPlayingCatchup()) {
          returnToLive();
          return;
        }

        // 4. Close channel menu
        if (showChannelMenu()) {
          closeChannelMenu(true);
          return;
        }

        // 5. Close PIN entry
        if (showPinEntry()) {
          setShowPinEntry(false);
          setPendingChannelLoad(null);
          setIsChannelLocked(false);
          setPinErrorMessage("");
          const previousChannel = channelBeforePinEntry();
          if (previousChannel !== null) {
            setCurrentIndex(previousChannel);
            setLiveChannel(data()[previousChannel]);
            setCurrentStream(data()[previousChannel].stream_id);
            setChannelBeforePinEntry(null);
            setShowOSD(true);
            resetOSDTimeout();
          }
          if (showChannelMenu()) closeChannelMenu(true);
          return;
        }

        // 6. Double-press to exit
        if (!escapePressed()) {
          setEscapePressed(true);
          setShowExitToast(true);
          clearTimeout(exitToastTimeout);
          exitToastTimeout = setTimeout(() => {
            setShowExitToast(false);
            setEscapePressed(false);
          }, 4000);
          return;
        }

        clearTimeout(exitToastTimeout);
        setShowExitToast(false);
        setEscapePressed(false);
        await handleExitNavigation();
      }}

      onEnter={handleRootEnter}

      // Up/Down: show vertical channel carousel, navigate index, play on key-up or after 800ms debounce
      onDown={() => {
        if (!showPinEntry()) showCarouselAndNavigate("down");
      }}
      onUp={() => {
        if (!showPinEntry()) showCarouselAndNavigate("up");
      }}

      // Left/Right: ALWAYS open channel menu — even if catchup is open.
      // openChannelMenu() closes catchup first internally.
      onLeft={() => {
        if (!showChannelMenu()) openChannelMenu();
      }}
      onRight={() => {
        if (!showChannelMenu()) openChannelMenu();
      }}

      autofocus
    >
      {/* ── Exit Toast ── */}
      <Show when={showExitToast()}>
        <View x={60} y={60} width={500} height={100} color={hexColor("#000000")} alpha={0.9} borderRadius={15} style={{ zIndex: 2000 }}>
          <View x={0} y={0} width={600} height={100} color={hexColor("#FF5722")} alpha={0.2} borderRadius={15} />
          <Text x={100} y={25} fontSize={28} fontWeight="bold" color={hexColor("#FFFFFF")} style={{ textAlign: "center" }}>
            Press Back Again to Exit
          </Text>
          <Text x={120} y={60} fontSize={18} color={hexColor("#CCCCCC")} style={{ textAlign: "center" }}>
            Press once more to return home
          </Text>
        </View>
      </Show>

      {/* ── Numeric Channel Dial Overlay ── */}
      <Show when={numericInput().length > 0}>
        <View x={1620} y={40} width={260} height={130} color={hexColor("#000000")} alpha={0.88} borderRadius={20} style={{ zIndex: 2000 }}>
          <View x={0} y={0} width={260} height={130} color={hexColor(clientBranding().primaryColor)} alpha={0.25} borderRadius={20} />
          <Text x={0} y={12} width={260} style={{ fontSize: 20, color: hexColor("#90CAF9"), textAlign: "center", fontFamily: "Roboto" }}>
            Channel
          </Text>
          <Text x={0} y={44} width={260} style={{ fontSize: 58, fontWeight: "bold", color: hexColor("#FFFFFF"), textAlign: "center", fontFamily: "Roboto" }}>
            {numericInput()}
          </Text>
        </View>
      </Show>

      {/* ── PIN Entry ── */}
      <Show when={showPinEntry() && !isNavigatingAway()}>
        <View color={"#000"} width={1920} height={1080}>
          <View x={760} y={150}>
            <Text fontSize={48} color={"#ffffff"} textAlign={"center"} x={-120}>Adult Content - PIN Required</Text>
            <Text fontSize={32} color={"#cccccc"} textAlign={"center"} y={60} x={-60}>Please enter your PIN to continue</Text>
            <Show when={pinErrorMessage()}>
              <View x={-130} y={640} width={600} height={60} color={hexColor("#ff000080")} borderRadius={10}>
                <Text fontSize={24} color={"#ffffff"} textAlign={"center"} x={150} y={18}>{pinErrorMessage()}</Text>
              </View>
            </Show>
          </View>
          <ConfirmPin onPinSet={handlePinConfirmed} onPinFailed={handlePinFailed} showBackground={false} title={' '} x={600} y={120} />
        </View>
      </Show>

      {/* ── Channel Carousel (dpad/channel up-down) ── */}
      {/* <Show when={showChannelCarousel() && !showPinEntry() && !showChannelMenu()}>
        <ChannelCarousel channels={data()} currentIndex={currentIndex()} />
      </Show> */}

      {/* ── OSD (commented — replaced by EpgGuideView) ── */}
      <Show when={showOSD() && !showPinEntry() && !showChannelMenu() && numericInput().length === 0 && !showCatchupCarousel() && !showChannelCarousel()}>
        <OSD channel={liveChannel()} isLocked={isChannelLocked()} />
      </Show>

      {/* ── Catchup available button (commented — no longer needed) ── */}
      {/* ── Catchup playback banner ── */}
      <Show when={isPlayingCatchup() && catchupProgram()}>
        <View x={60} y={30} width={460} height={54} zIndex={2010}>
          <View x={0} y={0} width={460} height={54} color={hexColor("#B71C1C")} alpha={0.18} borderRadius={12} />
          <Text x={16} y={14} style={{ fontSize: 21, color: hexColor("#EF9A9A"), fontFamily: "Roboto", fontWeight: "700" }}>
            ⏮ CATCH UP  •  Back to return to live
          </Text>
        </View>
      </Show>

      {/* ── EPG Guide View — replaces OSD, visible for 8 s then auto-hides ── */}
      {/* <EpgGuideView
        channel={liveChannel()}
        isVisible={showOSD() && !showPinEntry() && !showChannelMenu() && !showChannelCarousel() && numericInput().length === 0}
        onNavigate={() => resetOSDTimeout()}
        onPlayCatchup={(program) => {
          lockCatchupEnter();
          handlePlayCatchup(program);
        }}
        onPlayLive={() => {
          setShowOSD(false);
          clearTimeout(timeoutId);
          restoreRootFocus();
        }}
        onClose={() => {
          setShowOSD(false);
          clearTimeout(timeoutId);
          restoreRootFocus();
        }}
      /> */}

      {/* ── Channel Menu ── */}
      <Show when={showChannelMenu() && !showPinEntry()}>
        <View width={1920} height={1080} style={{ zIndex: 1100, pointerEvents: "none" }}>
          <View x={0} y={650} width={1920} height={430}>
            <View width={1920} height={430} color={hexColor("#000000")} alpha={0.92} />
            <View x={80} y={30}>
              <Text style={{ fontSize: 28, fontWeight: "500", color: hexColor("#ffffff"), fontFamily: "Roboto" }}>All Channels</Text>
              <Text x={200} y={5} style={{ fontSize: 18, color: hexColor("#9E9E9E"), fontFamily: "Roboto" }}>{data().length} available</Text>
            </View>

            <View x={60} y={80} style={{ pointerEvents: "auto" }}>
              <LazyUp
                ref={el => (channelMenuRef = el)}
                component={Row}
                autofocus
                gap={25}
                scroll="always"
                selected={selectedMenuIndex()}
                each={data()}
                upCount={10}
                onSelectedChanged={(index) => setSelectedMenuIndex(index)}
              >
                {(channel, index) => {
                  const channelData = channel();
                  const isCurrentlyPlaying = index === currentIndex();
                  const isAdult = isAdultChannel(channelData);

                  return (
                    <View
                      onEnter={() => {
                        setCurrentIndex(index);
                        setLiveChannel(channelData);
                        if (needsPinVerification(channelData)) {
                          setIsChannelLocked(true);
                          setShowPinEntry(true);
                          setPendingChannelLoad(channelData);
                        } else {
                          loadNewStream(channelData.stream_id);
                          closeChannelMenu(true);
                        }
                      }}
                      onFocus={() => {
                        console.log(`🔍 Channel ${index} focused: ${channelData.name}${isAdult ? ' 🔒' : ''}`);
                      }}
                      style={{
                        width: 280,
                        height: 240,
                        borderRadius: 12,
                        border: { width: 0, color: hexColor("#ffffff00") },
                        color: hexColor("#2C2C2C"),
                        focus: { border: { width: 4, color: hexColor("#FFFFFF") } }
                      }}
                    >
                      <View x={15} y={15} width={250} height={140} borderRadius={8} color={hexColor("#1E1E1E")}>
                        <Show when={channelData.stream_icon} fallback={
                          <View width={250} height={140}>
                            <Text x={125} y={55} style={{ fontSize: 56, fontWeight: "bold", color: hexColor("#424242"), textAlign: "center", fontFamily: "Roboto" }}>
                              {channelData.num}
                            </Text>
                          </View>
                        }>
                          <View src={fixImageUrl(channelData.stream_icon)} width={200} height={200} x={25} y={-30} />
                        </Show>
                        <Show when={isAdult}>
                          <View x={200} y={10} width={40} height={40} color={hexColor("#FF0000")} borderRadius={20} alpha={0.9}>
                            <Text x={11} y={8} style={{ fontSize: 24, color: hexColor("#ffffff") }}>🔒</Text>
                          </View>
                        </Show>
                      </View>

                      <View x={15} y={165}>
                        <View width={60} height={32} color={hexColor(clientBranding().primaryColor)} borderRadius={6}>
                          <Text x={23} y={7} style={{ fontSize: 18, fontWeight: "bold", color: hexColor("#ffffff"), textAlign: "center", fontFamily: "Roboto" }}>
                            {channelData.num}
                          </Text>
                        </View>
                        <Show when={isCurrentlyPlaying}>
                          <View x={70} y={0} width={90} height={32} color={hexColor("#00C853")} borderRadius={16}>
                            <Text x={16} y={10} style={{ fontSize: 13, fontWeight: "bold", color: hexColor("#000000"), fontFamily: "Roboto" }}>● PLAYING</Text>
                          </View>
                        </Show>
                        <Show when={isAdult && !isCurrentlyPlaying}>
                          <View x={70} y={0} width={70} height={32} color={hexColor("#FF0000")} borderRadius={16}>
                            <Text x={12} y={10} style={{ fontSize: 13, fontWeight: "bold", color: hexColor("#ffffff"), fontFamily: "Roboto" }}>🔒 18+</Text>
                          </View>
                        </Show>
                      </View>

                      <Text x={15} y={205} style={{ fontSize: 16, fontWeight: "500", color: hexColor("#ffffff"), width: 250, fontFamily: "Roboto" }}>
                        {channelData.name?.length > 20 ? `${channelData.name.substring(0, 20)}...` : channelData.name}
                      </Text>
                    </View>
                  );
                }}
              </LazyUp>
            </View>
          </View>
        </View>
      </Show>

      {/* ── Loading ── */}
      <Show when={isBuffering() && !showPinEntry()}>
        <View x={850} y={400} zIndex={1001} width={220} height={220} borderRadius={20}>
          <View><LoadingIndicator /></View>
        </View>
      </Show>
    </View>
  );
};

export default LiveTvPage;